<template>
	<CModal title="Modifier le produit" size="lg" :show.sync="showModal">
		<CRow v-if="orderItem && orderItem.id">
			<CCol col="6">
				<CInput label="N° de colis" :value="orderItem.id" disabled />
			</CCol>
			<CCol col="6">
				<CInput label="N° de bordereau" :value="orderItem.slip.identifier" disabled />
			</CCol>

			<CCol col="6">
				<div class="form-group">
					<label>Produit</label>
					<GTreeView
						input-name="product"
						v-model="formProduct"
						placeholder="Rechercher un produit"
						:multiple="false"
						:default-options="true"
						:load-options="loadProducts"
						:async="true" />
				</div>
			</CCol>

			<CCol col="6">
				<CInput v-model="orderItem.weightNet" name="weightNet" label="Poids net" />
			</CCol>

			<CCol col="6">
				<div class="form-group">
					<label>Point de vente</label>
					<GTreeView
						input-name="slip.salePts"
						v-model="formPointOfSale"
						placeholder="Point de vente"
						:multiple="false"
						:default-options="true"
						:load-options="loadSalePoints"
						:async="true" />
				</div>
			</CCol>

			<CCol col="6">
				<div class="form-group">
					<label>Point de dépôt</label>
					<GTreeView
						input-name="slip.depotPts"
						v-model="formPointOfDepot"
						placeholder="Point de dépôt"
						:multiple="false"
						:default-options="true"
						:load-options="loadDropoffPoints"
						:async="true" />
				</div>
			</CCol>

			<CCol col="6">
				<CInput
					v-model="orderItem.declaredPrice"
					name="declaredPrice"
					label="Valeur du contenu (max 45€)" />
			</CCol>

			<CCol col="12">
				<CInput v-model="formComponentName" label="Description du contenu" />
			</CCol>

			<CCol col="12">
				<hr />
			</CCol>

			<CCol col="6">
				<p>
					Poids supplémentaire: <b>{{ kgSupp }} KG</b>
				</p>
			</CCol>
			<CCol col="6">
				<p>
					Tarif poids supplémentaire: <b>{{ priceSupp }} €</b>
				</p>
			</CCol>

			<CCol col="6">
				<p>
					Prix de base du colis: <b>{{ orderItem.product.price.toFixed(2) }} €</b>
				</p>
			</CCol>

			<CCol col="6">
				<p>
					Prix total suggéré avec le poids supplémentaire: <b>{{ suggestedPrice.toFixed(2) }} €</b>
				</p>
			</CCol>

			<CCol col="6">
				<CInput v-model="formPrice" label="Prix">
					<template #append-content><CIcon name="cil-euro" /></template>
				</CInput>
			</CCol>
		</CRow>

		<template #footer>
			<CButton color="secondary" v-on:click="showModal = false">Annuler</CButton>
			<GLoadingButton :loading="loading" color="success" v-on:click="onSubmit" label="Sauvegarder" />
		</template>
	</CModal>
</template>

<script>
	import GTreeView from '../../../../../components/ghostlexly-components/GTreeView';
	import GLoadingButton from '../../../../../components/ghostlexly-components/GLoadingButton';
	export default {
		name: 'edit-order-item',
		components: { GLoadingButton, GTreeView },

		data() {
			return {
				orderItem: {},
				showModal: false,
				loading: false,
				formProduct: null,
				formPointOfSale: null,
				formPointOfDepot: null,
				formComponentName: null,
				formPrice: 0,
			};
		},

		watch: {
			orderItem: function (newVal) {
				if (!newVal) {
					return false;
				}

				if (newVal.product) {
					this.formProduct = newVal.product['@id'];
				}

				if (newVal.slip) {
					if (newVal.slip.salePts) {
						this.formPointOfSale = newVal.slip.salePts['@id'];
					}

					if (newVal.slip.depotPts) {
						this.formPointOfDepot = newVal.slip.depotPts['@id'];
					}
				}
			},
		},

		computed: {
			suggestedPrice() {
				if (this.orderItem && this.orderItem.product) {
					let total = this.orderItem.product.price + 10 * this.kgSupp;

					if (total < this.orderItem.product.price) {
						return this.orderItem.product.price;
					} else {
						return total;
					}
				}

				return 0;
			},

			kgSupp() {
				const diff =
					Number(this.orderItem.weightNet.toString().replace(/,/g, '.')) -
					Number(this.orderItem.product.weightNet.toString().replace(/,/g, '.'));
				return diff <= 0.5 ? 0 : Math.round(diff);
			},

			priceSupp() {
				const total = Number((10 * this.kgSupp).toFixed(2));
				return total < 0 ? 0 : total;
			},
		},

		methods: {
			async onSubmit() {
				this.loading = true;

				// update slip depot & sale infos
				try {
					await this.$axios
						.patch(`/api/slips/${this.orderItem.slip.identifier}`, {
							salePts: this.formPointOfSale,
							depotPts: this.formPointOfDepot,
						})
						.catch(() => {
							this.$toast.error(
								`Le bordereau "${this.orderItem.slip.identifier}" n'est pas modifiable.`
							);
							throw 'Bordereau non-modifiable';
						});
				} catch (err) {
					return false;
				}

				// update component name
				try {
					await this.$axios
						.patch(this.orderItem.components[0]['@id'], {
							name: this.formComponentName,
							weightNet: this.orderItem.weightNet,
							weightGross: this.orderItem.weightNet,
						})
						.catch(() => {
							this.$toast.error(`Le composant [0] n'est pas modifiable.`);
							throw "Composant [0] n'est pas modifiable";
						});
				} catch (err) {
					return false;
				}

				// update order item
				try {
					await this.$axios
						.patch(this.orderItem['@id'], {
							product: this.formProduct,
							weightNet: this.orderItem.weightNet,
							weightGross: this.orderItem.weightNet,
							declaredPrice: this.orderItem.declaredPrice,
							price: this.formPrice,
							priceTTC: this.formPrice,
						})
						.then(() => {
							this.$formDataToApi.clearPrecedentErrors();
							this.$emit('refresh');
							this.loading = false;
							this.showModal = false;
						})
						.catch(err => {
							this.$formDataToApi.responseErrorProcess(err);
							this.loading = false;
							throw "Ce produit n'est pas modifiable.";
						});
				} catch (err) {
					return false;
				}
			},

			loadSalePoints({ action, searchQuery, callback }) {
				if (action === 'ASYNC_SEARCH') {
					let params = new URLSearchParams();
					params.append('search_autocomplete', searchQuery);
					params.append('isPointOfSale', '1');
					params.append('itemsPerPage', '9999');

					return this.$axios.get(`/api/point_of_sales?${params.toString()}`).then(res => {
						const options = res.data['hydra:member'].map(item => {
							let label = `[${item.reference}] ${item.name} - ${item.postalCode}, ${item.city}`;

							return {
								'@id': item['@id'],
								name: label,
							};
						});

						if (action === 'ASYNC_SEARCH') {
							callback(null, options);
						}
					});
				}
			},

			loadDropoffPoints({ action, searchQuery, callback }) {
				if (action === 'ASYNC_SEARCH') {
					let params = new URLSearchParams();
					params.append('search_autocomplete', searchQuery);
					params.append('isPointOfDepot', '1');

					return this.$axios.get(`/api/point_of_sales?${params.toString()}`).then(res => {
						const options = res.data['hydra:member'].map(item => {
							let label = `[${item.reference}] ${item.name} - ${item.postalCode}, ${item.city}`;

							return {
								'@id': item['@id'],
								name: label,
							};
						});

						if (action === 'ASYNC_SEARCH') {
							callback(null, options);
						}
					});
				}
			},

			loadProducts({ action, searchQuery, callback }) {
				if (action === 'ASYNC_SEARCH') {
					let params = new URLSearchParams();
					params.append('search_autocomplete', searchQuery);

					return this.$axios.get(`/api/products?${params.toString()}`).then(res => {
						const options = res.data['hydra:member'].map(item => {
							let label = item.name;

							return {
								'@id': item['@id'],
								name: label,
							};
						});

						if (action === 'ASYNC_SEARCH') {
							callback(null, options);
						}
					});
				}
			},
		},
	};
</script>
